import { useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useGlobalContext } from "../../GlobalContext";

export default function useDriverConfig() {
  const { baseurl, token, AdminId, setIsUnauthorised } =
    useGlobalContext();

  const [configRows, setConfigRows] = useState([]);
  const [configLoading, setConfigLoading] = useState(false);

  /* ================= GET ALL DRIVER CONFIGS ================= */

  const fetchAllDriverConfigs = useCallback(
    async (silent = false) => {
      if (!silent) setConfigLoading(true);

      try {
        const res = await axios.post(
          `${baseurl}/api/Driver/v1.0/getAllDriverConfigDetails`,
          { adminId: AdminId, driverCode: "" },
          { headers: { Authorization: `Basic ${token}` } }
        );

        if (res.data.responseCode === "00") {
          setConfigRows(res.data.data || []);
        } else if (res.data.responseCode === "401") {
          setIsUnauthorised(true);
        } else {
          toast.error(
            res.data.responseMessage || "Failed to fetch configs"
          );
        }
      } catch {
        toast.error("Error fetching driver configurations");
      } finally {
        if (!silent) setConfigLoading(false);
      }
    },
    [baseurl, token, AdminId, setIsUnauthorised]
  );

  /* ================= DELETE CONFIG (LIST VIEW) ================= */

  const handleDeleteConfig = async (configId) => {
    const ok = window.confirm(
      "Are you sure you want to delete this config?"
    );
    if (!ok) return;

    try {
      const res = await axios.post(
        `${baseurl}/api/Driver/v1.0/deleteDriverConfigDetails`,
        { adminId: AdminId, configId: String(configId) },
        { headers: { Authorization: `Basic ${token}` } }
      );

      if (res.data.responseCode === "00") {
        toast.success(res.data.responseMessage || "Deleted");
        fetchAllDriverConfigs(true);
      } else if (res.data.responseCode === "401") {
        setIsUnauthorised(true);
      } else {
        toast.error(res.data.responseMessage || "Delete failed");
      }
    } catch {
      toast.error("Error deleting config");
    }
  };

  /* ================= GET CONFIG BY DRIVER CODE ================= */

  const fetchDriverConfigByCode = async (
    driverCode,
    setFetching,
    setConfig
  ) => {
    if (!driverCode) return;

    setFetching(true);
    try {
      const payload = { adminId: AdminId, driverCode };

      const res = await axios.post(
        `${baseurl}/api/Driver/v1.0/getAllDriverConfigDetails`,
        payload,
        { headers: { Authorization: `Basic ${token}` } }
      );

      if (res.data.responseCode === "00") {
        const row =
          (res.data.data || []).find(
            (x) => x.driver_code === driverCode
          ) || null;

        setConfig(row);
      } else if (res.data.responseCode === "401") {
        setIsUnauthorised(true);
      } else {
        toast.error(
          res.data.responseMessage || "Failed to fetch config"
        );
      }
    } catch {
      toast.error("Error fetching config");
    } finally {
      setFetching(false);
    }
  };

  /* ================= QUICK ADD CONFIG ================= */

  const fetchDriverConfigForQuickAdd = async (
    driverCode,
    setFetching,
    setExistingConfig,
    setForm
  ) => {
    if (!driverCode) return;

    setFetching(true);
    try {
      const payload = { adminId: AdminId, driverCode };

      const res = await axios.post(
        `${baseurl}/api/Driver/v1.0/getAllDriverConfigDetails`,
        payload,
        { headers: { Authorization: `Basic ${token}` } }
      );

      if (res.data.responseCode === "00") {
        const row =
          (res.data.data || []).find(
            (x) => x.driver_code === driverCode
          ) || null;

        setExistingConfig(row);

        if (row) {
          setForm({
            cashLimit: row.cash_limit?.toString?.() ?? "",
            dailyTaskLimit:
              row.daily_task_limit?.toString?.() ?? "",
            concurrentOrder:
              row.concurrent_order?.toString?.() ?? "",
            maxTeamLimit:
              row.max_team_limit?.toString?.() ?? "",
          });
        } else {
          setForm({
            cashLimit: "",
            dailyTaskLimit: "",
            concurrentOrder: "",
            maxTeamLimit: "",
          });
        }
      } else if (res.data.responseCode === "401") {
        setIsUnauthorised(true);
      } else {
        toast.error(
          res.data.responseMessage ||
            "Failed to fetch existing config"
        );
      }
    } catch {
      toast.error("Error fetching existing config");
    } finally {
      setFetching(false);
    }
  };

  const saveDriverQuickConfig = async (payload) => {
    try {
      const res = await axios.post(
        `${baseurl}/api/Driver/v1.0/insertDriverConfigDetails`,
        payload,
        { headers: { Authorization: `Basic ${token}` } }
      );

      if (res.data.responseCode === "00") {
        toast.success(res.data.responseMessage || "Saved");
        return true;
      }

      if (res.data.responseCode === "401") {
        setIsUnauthorised(true);
        return false;
      }

      toast.error(res.data.responseMessage || "Failed to save");
      return false;
    } catch {
      toast.error("Error saving config");
      return false;
    }
  };

  const deleteDriverQuickConfig = async (configId) => {
    try {
      const payload = {
        adminId: AdminId,
        configId: String(configId),
      };

      const res = await axios.post(
        `${baseurl}/api/Driver/v1.0/deleteDriverConfigDetails`,
        payload,
        { headers: { Authorization: `Basic ${token}` } }
      );

      if (res.data.responseCode === "00") {
        toast.success(res.data.responseMessage || "Deleted");
        return true;
      }

      if (res.data.responseCode === "401") {
        setIsUnauthorised(true);
        return false;
      }

      toast.error(res.data.responseMessage || "Failed to delete");
      return false;
    } catch {
      toast.error("Error deleting config");
      return false;
    }
  };

  /* ================= SINGLE DRIVER MODAL CONFIG ================= */

  const fetchSingleDriverConfig = async (
    driverCode,
    setFetching,
    setExistingConfig,
    setForm
  ) => {
    if (!driverCode) return;

    setFetching(true);
    try {
      const payload = { adminId: AdminId, driverCode };

      const res = await axios.post(
        `${baseurl}/api/Driver/v1.0/getAllDriverConfigDetails`,
        payload,
        { headers: { Authorization: `Basic ${token}` } }
      );

      if (res.data.responseCode === "00") {
        const row =
          (res.data.data || []).find(
            (x) => x.driver_code === driverCode
          ) || null;

        setExistingConfig(row);

        if (row) {
          setForm({
            cashLimit: row.cash_limit?.toString?.() ?? "",
            dailyTaskLimit:
              row.daily_task_limit?.toString?.() ?? "",
            concurrentOrder:
              row.concurrent_order?.toString?.() ?? "",
            maxTeamLimit:
              row.max_team_limit?.toString?.() ?? "",
          });
        } else {
          setForm({
            cashLimit: "",
            dailyTaskLimit: "",
            concurrentOrder: "",
            maxTeamLimit: "",
          });
        }
      } else if (res.data.responseCode === "401") {
        setIsUnauthorised(true);
      } else {
        toast.error(
          res.data.responseMessage || "Failed to fetch config"
        );
      }
    } catch {
      toast.error("Error fetching driver config");
    } finally {
      setFetching(false);
    }
  };

  const saveSingleDriverConfig = async (payload) => {
    try {
      const res = await axios.post(
        `${baseurl}/api/Driver/v1.0/insertDriverConfigDetails`,
        payload,
        { headers: { Authorization: `Basic ${token}` } }
      );

      if (res.data.responseCode === "00") {
        toast.success(res.data.responseMessage || "Saved");
        return true;
      }

      if (res.data.responseCode === "401") {
        setIsUnauthorised(true);
        return false;
      }

      toast.error(
        res.data.responseMessage || "Failed to save config"
      );
      return false;
    } catch {
      toast.error("Error saving config");
      return false;
    }
  };

  const deleteSingleDriverConfig = async (configId) => {
    try {
      const payload = {
        adminId: AdminId,
        configId: String(configId),
      };

      const res = await axios.post(
        `${baseurl}/api/Driver/v1.0/deleteDriverConfigDetails`,
        payload,
        { headers: { Authorization: `Basic ${token}` } }
      );

      if (res.data.responseCode === "00") {
        toast.success(res.data.responseMessage || "Deleted");
        return true;
      }

      if (res.data.responseCode === "401") {
        setIsUnauthorised(true);
        return false;
      }

      toast.error(
        res.data.responseMessage || "Failed to delete"
      );
      return false;
    } catch {
      toast.error("Error deleting config");
      return false;
    }
  };

  return {
    configRows,
    configLoading,
    fetchAllDriverConfigs,
    handleDeleteConfig,

    fetchDriverConfigByCode,
    fetchDriverConfigForQuickAdd,
    saveDriverQuickConfig,
    deleteDriverQuickConfig,

    fetchSingleDriverConfig,
    saveSingleDriverConfig,
    deleteSingleDriverConfig,
  };
}
